// App.js - Обновленное приложение с новым Header
import React from 'react';
import { BrowserRouter } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { PurchaseProvider } from './context/PurchaseContext';
import { ContractProvider } from './context/ContractContext';
import { ValidationProvider } from './context/ValidationContext';
import AppRoutesUpdated from './routes/AppRoutesUpdated';
import Header from './components/Layout/Header';
import Footer from './components/Layout/Footer';
import './App.css';
import './styles/Layout.css';
import './styles/ContractSections.css'; // <-- Добавленный импорт

/**
 * Компонент обработки ошибок
 */
class AppErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        console.error('Ошибка приложения:', error, errorInfo);
        this.setState({ error: error, errorInfo: errorInfo });
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="app-error-boundary">
                    <div className="error-boundary-container">
                        <div className="error-boundary-icon">😞</div>
                        <h2 className="error-boundary-title"> Что-то пошло не так </h2>
                        <p className="error-boundary-message"> Произошла непредвиденная ошибка. Попробуйте перезагрузить страницу. </p>
                        <button onClick={() => window.location.reload()} className="error-boundary-reload-btn" > Перезагрузить страницу </button>
                        {/* Детали ошибки в режиме разработки */}
                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <details className="error-boundary-details">
                                <summary> Детали ошибки (режим разработки) </summary>
                                <pre>
                                    {this.state.error && this.state.error.toString()}
                                    <br />
                                    {this.state.errorInfo.componentStack}
                                </pre>
                            </details>
                        )}
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

/**
 * Компонент для отслеживания производительности приложения
 */
const PerformanceMonitor = ({ children }) => {
    React.useEffect(() => {
        const startTime = performance.now();
        const handleLoad = () => {
            const loadTime = performance.now() - startTime;
            console.log(`Приложение загружено за ${loadTime.toFixed(2)}мс`);
            if (process.env.NODE_ENV === 'production') {
                // analytics.track('app_load_time', { duration: loadTime });
            }
        };

        if (document.readyState === 'complete') {
            handleLoad();
        } else {
            window.addEventListener('load', handleLoad);
            return () => window.removeEventListener('load', handleLoad);
        }
    }, []);

    return children;
};

/**
 * Компонент для обработки офлайн/онлайн состояния
 */
const NetworkMonitor = ({ children }) => {
    const [, setIsOnline] = React.useState(navigator.onLine);
    const [showOfflineNotification, setShowOfflineNotification] = React.useState(false);

    React.useEffect(() => {
        const handleOnline = () => {
            setIsOnline(true);
            setShowOfflineNotification(false);
            console.log('Соединение с интернетом восстановлено');
        };

        const handleOffline = () => {
            setIsOnline(false);
            setShowOfflineNotification(true);
            console.log('Соединение с интернетом потеряно');
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    return (
        <>
            {children}
            {/* Уведомление об офлайн режиме */}
            {showOfflineNotification && (
                <div className="network-notification">
                    <span className="network-notification-icon">📴</span>
                    <span>Нет подключения к интернету</span>
                    <button onClick={() => setShowOfflineNotification(false)} className="network-notification-close" > × </button>
                </div>
            )}
        </>
    );
};

/**
 * Компонент для условного отображения Header
 */
const AppContent = () => {
    return (
        <div className="app">
            <Header />
            <main className="app-main">
                <AppRoutesUpdated />
            </main>
            <Footer />
        </div>
    );
};

/**
 * Главный компонент приложения
 */
const App = () => {
    React.useEffect(() => {
        console.log('🚀 Генератор договоров запущен');
        console.log('📅 Версия:', process.env.REACT_APP_VERSION || '1.0.0');
        console.log('🔧 Режим:', process.env.NODE_ENV);
        document.title = 'Генератор договоров - Создавайте юридические документы быстро и надежно';

        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
            metaDescription.setAttribute('content', 'Создавайте договоры аренды, займа и другие юридические документы с помощью удобного конструктора. Быстро, надежно, юридически корректно.');
        }
    }, []);

    return (
        <AppErrorBoundary>
            <PerformanceMonitor>
                <NetworkMonitor>
                    <BrowserRouter>
                        <AuthProvider>
                            <PurchaseProvider>
                                <ContractProvider>
                                    <ValidationProvider>
                                        <AppContent />
                                    </ValidationProvider>
                                </ContractProvider>
                            </PurchaseProvider>
                        </AuthProvider>
                    </BrowserRouter>
                </NetworkMonitor>
            </PerformanceMonitor>
        </AppErrorBoundary>
    );
};

export default App;